import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { hostname, isSafeUrl } from "../../../utils";
import { INTEGRATION_NAMES } from "../constants";

// Integration types that require page selection
const INTEGRATIONS_REQUIRING_PAGE_SELECTION = ["facebook", "instagram", "linkedin", "google_analytics"];

interface PageLocation {
  city?: string;
  state?: string;
  country?: string;
  street?: string;
  zip?: string;
}

interface Page {
  id: string;
  name: string;
  category?: string;
  picture?: string;
  pageAccessToken?: string;
  username?: string;
  followers?: number;
  facebookPageId?: string;
  facebookPageName?: string;
  // Location fields to distinguish pages with same name
  location?: PageLocation;
  singleLineAddress?: string;
  link?: string;
  // LinkedIn-specific fields
  type?: "personal" | "organization";
  authorUrn?: string;
  email?: string;
  vanityName?: string;
  industry?: string;
  // Google Analytics-specific fields
  propertyDefaultUri?: string;
}

/**
 * Format location info for display to help distinguish pages with same name
 */
const formatPageLocation = (page: Page): string => {
  // Prefer single line address if available
  if (page.singleLineAddress) {
    return page.singleLineAddress;
  }
  // Build location string from location object
  if (page.location) {
    const parts: string[] = [];
    if (page.location.city) parts.push(page.location.city);
    if (page.location.state) parts.push(page.location.state);
    if (parts.length > 0) return parts.join(", ");
  }
  return "";
};

/**
 * Build the secondary text for a page item
 */
const getPageSecondaryText = (page: Page, integrationType: string): string => {
  const parts: string[] = [];

  if (integrationType === "instagram") {
    if (page.username) parts.push(`@${page.username}`);
    if (page.followers) parts.push(`${page.followers.toLocaleString()} followers`);
    // Add location from parent Facebook page
    const location = formatPageLocation(page);
    if (location) parts.push(location);
    return parts.join(" • ");
  }

  if (integrationType === "linkedin") {
    if (page.type === "personal") {
      return "Personal Profile";
    }
    const linkedinParts: string[] = ["Company Page"];
    if (page.industry) linkedinParts.push(page.industry);
    return linkedinParts.join(" • ");
  }

  if (integrationType === "google_analytics") {
    const gaParts: string[] = [];
    if (page.category) gaParts.push(page.category);
    if (page.propertyDefaultUri) gaParts.push(page.propertyDefaultUri);
    return gaParts.join(" • ");
  }

  // Facebook - show category, username, and location
  if (page.category) parts.push(page.category);
  if (page.username) parts.push(`@${page.username}`);
  const location = formatPageLocation(page);
  if (location) parts.push(location);

  return parts.join(" • ");
};

interface ComposioOAuthFormProps {
  editMode: boolean;
  initialData?: {
    composioConnectedAccountId?: string;
    connectedAt?: string;
    composioToolkit?: string;
    pageId?: string;
    pageName?: string;
  };
  companyId: string;
  instanceName: string;
  integrationType: string;
  getToken: () => Promise<string>;
  onValidationChange: (isValid: boolean) => void;
  onDataChange: (data: any) => void;
}

/**
 * Generic OAuth form component for Composio-based integrations.
 *
 * This component handles the OAuth flow for any integration that uses
 * Composio's managed OAuth service (Facebook, Instagram, Twitter, etc.).
 *
 * The flow:
 * 1. User clicks "Connect Account"
 * 2. Backend initiates OAuth with Composio
 * 3. Popup opens for user to authorize
 * 4. On popup close, we check if connection was successful
 * 5. For Facebook/Instagram: User selects a page
 * 6. User clicks Install to save the integration
 */
export default function ComposioOAuthForm({
  editMode,
  initialData = {},
  companyId,
  instanceName,
  integrationType,
  getToken,
  onValidationChange,
  onDataChange,
}: ComposioOAuthFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingPages, setIsLoadingPages] = useState(false);
  const [error, setError] = useState("");
  const [isConnected, setIsConnected] = useState(false);
  const [stateToken, setStateToken] = useState<string | null>(null);
  const [composioData, setComposioData] = useState<{
    composioConnectedAccountId?: string;
    composioToolkit?: string;
  }>({});

  // Page selection state
  const [pages, setPages] = useState<Page[]>([]);
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);

  // Manual entry state (for LinkedIn)
  const [allowManualEntry, setAllowManualEntry] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualAuthorUrn, setManualAuthorUrn] = useState("");
  const [manualEntryHelp, setManualEntryHelp] = useState<{
    description?: string;
    personal?: string;
    organization?: string;
    howToFind?: string;
  } | null>(null);

  // Check if this integration type requires page selection
  const requiresPageSelection = INTEGRATIONS_REQUIRING_PAGE_SELECTION.includes(
    integrationType.toLowerCase()
  );

  // Get display name for the integration
  const displayName =
    INTEGRATION_NAMES[integrationType] ||
    integrationType.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

  // Check if already connected (edit mode)
  useEffect(() => {
    if (editMode && initialData.composioConnectedAccountId) {
      setIsConnected(true);
      // If there's already a page selected, set it
      if (initialData.pageId) {
        setSelectedPage({
          id: initialData.pageId,
          name: initialData.pageName || "",
        });
      }
    }
  }, [editMode, initialData.composioConnectedAccountId, initialData.pageId, initialData.pageName]);

  // Validate manual URN format
  const isValidManualUrn = manualAuthorUrn.startsWith("urn:li:person:") ||
    manualAuthorUrn.startsWith("urn:li:organization:");

  // Determine if form is valid
  // For integrations requiring page selection: must be connected AND have a page selected OR valid manual entry
  // For other integrations: just need to be connected
  const isFormValid = requiresPageSelection
    ? isConnected && (selectedPage !== null || (allowManualEntry && isValidManualUrn))
    : isConnected;

  // Update parent when state changes
  useEffect(() => {
    onValidationChange(isFormValid);
    onDataChange({
      connected: isConnected,
      stateToken,
      selectedPage,
      manualAuthorUrn: isValidManualUrn ? manualAuthorUrn : undefined,
      ...composioData,
    });
  }, [isFormValid, isConnected, stateToken, composioData, selectedPage, manualAuthorUrn, isValidManualUrn, onValidationChange, onDataChange]);

  // Fetch pages after OAuth connection
  const fetchPages = useCallback(
    async (connectedAccountId: string) => {
      setIsLoadingPages(true);
      setError("");

      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/integrations/composio/pages`,
          {
            params: {
              connectedAccountId,
              integrationType,
            },
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (response.data.pages) {
          setPages(response.data.pages);
          // Auto-select if only one page
          if (response.data.pages.length === 1) {
            setSelectedPage(response.data.pages[0]);
          }
        }

        // Handle manual entry option (for LinkedIn)
        if (response.data.allowManualEntry) {
          setAllowManualEntry(true);
          setManualEntryHelp(response.data.manualEntryHelp || null);

          // If pages are empty, automatically show manual entry form
          if (response.data.pagesEmpty) {
            setShowManualEntry(true);
          }
        }
      } catch (err: any) {
        console.error("Error fetching pages:", err);
        setError(
          err.response?.data?.error ||
            "Failed to fetch pages. Please try reconnecting."
        );
      } finally {
        setIsLoadingPages(false);
      }
    },
    [getToken, integrationType]
  );

  // Handle OAuth flow
  const handleConnect = useCallback(async () => {
    setIsLoading(true);
    setError("");
    setPages([]);
    setSelectedPage(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/integrations/composio/connect`,
        {
          companyId,
          integrationType,
          instanceName: instanceName || `${displayName} Account`,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response.data.redirectUrl) {
        // Store state token for completion
        setStateToken(response.data.stateToken);

        // Open OAuth in a popup window
        const width = 600;
        const height = 700;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;

        const safeUrlPattern = /^https?:\/\/[a-zA-Z0-9][a-zA-Z0-9.\-:/\\?&=%_+~#]+$/;
        if (!safeUrlPattern.test(response.data.redirectUrl)) return;
        const popup = window.open(
          response.data.redirectUrl,
          `${integrationType}OAuth`,
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,status=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        // Poll for popup close and check connection status
        const pollInterval = setInterval(async () => {
          if (popup?.closed) {
            clearInterval(pollInterval);

            // Check if connection was successful
            try {
              const currentToken = await getToken();
              const statusResponse = await axios.get(
                `${hostname}/integrations/composio/status?companyId=${companyId}&integrationType=${integrationType}`,
                {
                  headers: { Authorization: `Bearer ${currentToken}` },
                }
              );

              if (statusResponse.data.connected) {
                // Connection successful
                // Get the most recent active connection for this toolkit
                const connections = statusResponse.data.connections || [];
                const connectedAccount = connections.find(
                  (c: any) => c.status === "ACTIVE"
                );

                if (connectedAccount) {
                  // Store Composio data in state
                  setComposioData({
                    composioConnectedAccountId: connectedAccount.id,
                    composioToolkit: connectedAccount.toolkit,
                  });
                  setIsConnected(true);

                  // If this integration requires page selection, fetch pages
                  if (requiresPageSelection) {
                    await fetchPages(connectedAccount.id);
                  }
                } else {
                  setError("No active connection found. Please try again.");
                }
              } else {
                setError("Connection not completed. Please try again.");
              }
            } catch (err: any) {
              console.error("Error checking connection status:", err);
              setError(
                err.response?.data?.error ||
                  "Failed to verify connection. Please try again."
              );
            }

            setIsLoading(false);
          }
        }, 1000);

        // Check if popup was blocked
        if (!popup || popup.closed || typeof popup.closed === "undefined") {
          clearInterval(pollInterval);
          setError(
            "Popup was blocked. Please allow popups for this site and try again."
          );
          setIsLoading(false);
        }
      }
    } catch (err: any) {
      console.error(`Error starting ${integrationType} OAuth:`, err);
      setError(
        err.response?.data?.error ||
          err.message ||
          "Failed to start connection. Please try again."
      );
      setIsLoading(false);
    }
  }, [
    companyId,
    instanceName,
    integrationType,
    displayName,
    getToken,
    requiresPageSelection,
    fetchPages,
  ]);

  // Handle page selection
  const handlePageSelect = (page: Page) => {
    setSelectedPage(page);
  };

  // In edit mode with existing connection, show connected status
  if (editMode && isConnected) {
    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2 }}>
          {displayName} account connected
          {initialData.connectedAt && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Connected on:{" "}
              {new Date(initialData.connectedAt).toLocaleDateString()}
            </Typography>
          )}
          {initialData.pageName && (
            <Typography variant="caption" display="block">
              Page: {initialData.pageName}
            </Typography>
          )}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          To reconnect with a different account, remove this integration and
          create a new one.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Connect your {displayName} account with one click. No app setup or
          developer account required.
        </Typography>
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {!isConnected ? (
        <>
          <Button
            variant="contained"
            onClick={handleConnect}
            disabled={isLoading}
            startIcon={
              isLoading && <CircularProgress size={16} color="inherit" />
            }
            fullWidth
            size="large"
            sx={{ mb: 2 }}
          >
            {isLoading ? "Connecting..." : `Connect ${displayName} Account`}
          </Button>

          <Alert severity="warning">
            <Typography variant="caption">
              <strong>Note:</strong> A popup will open for you to authorize
              access. Make sure pop-ups are not blocked.
            </Typography>
          </Alert>
        </>
      ) : (
        <>
          <Alert severity="success" sx={{ mb: 2 }}>
            {displayName} connected successfully!
            {requiresPageSelection && pages.length > 0
              ? " Now select a page below."
              : " Click \"Install\" to save."}
          </Alert>

          {/* Page Selection UI */}
          {requiresPageSelection && (
            <Box sx={{ mt: 2 }}>
              {isLoadingPages ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, py: 2 }}>
                  <CircularProgress size={20} />
                  <Typography>Loading pages...</Typography>
                </Box>
              ) : pages.length > 0 ? (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    {integrationType === "instagram"
                      ? "Select an Instagram account:"
                      : integrationType === "linkedin"
                      ? "Select a profile or company page:"
                      : integrationType === "google_analytics"
                      ? "Select a GA4 property:"
                      : "Select a page:"}
                  </Typography>
                  <FormControl component="fieldset" fullWidth>
                    <RadioGroup
                      value={selectedPage?.id || ""}
                      onChange={(e) => {
                        const page = pages.find((p) => p.id === e.target.value);
                        if (page) handlePageSelect(page);
                      }}
                    >
                      <List sx={{ bgcolor: "background.paper", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                        {pages.map((page) => (
                          <ListItem key={page.id} disablePadding>
                            <ListItemButton
                              onClick={() => handlePageSelect(page)}
                              selected={selectedPage?.id === page.id}
                            >
                              <FormControlLabel
                                value={page.id}
                                control={<Radio />}
                                label=""
                                sx={{ mr: 0 }}
                              />
                              <ListItemAvatar>
                                <Avatar
                                  src={page.picture}
                                  alt={page.name}
                                  sx={{ width: 40, height: 40 }}
                                >
                                  {page.name?.[0]}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={page.name || page.username}
                                secondary={getPageSecondaryText(page, integrationType)}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </RadioGroup>
                  </FormControl>
                </>
              ) : (
                <>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      {integrationType === "instagram"
                        ? "No Instagram Business accounts found. Make sure you have an Instagram Business or Creator account connected to a Facebook Page."
                        : integrationType === "linkedin"
                        ? "No LinkedIn profiles or company pages found. This may be due to API rate limits. You can manually enter your LinkedIn URN below."
                        : integrationType === "google_analytics"
                        ? "No GA4 properties found. Make sure the connected Google account has access to at least one Google Analytics 4 property."
                        : "No pages found. Make sure you have admin access to at least one Facebook Page."}
                    </Typography>
                  </Alert>
                </>
              )}

              {/* Manual Entry Form for LinkedIn */}
              {allowManualEntry && integrationType === "linkedin" && (
                <Box sx={{ mt: 2 }}>
                  {pages.length > 0 && !showManualEntry && (
                    <Button
                      variant="text"
                      size="small"
                      onClick={() => setShowManualEntry(true)}
                      sx={{ mb: 1 }}
                    >
                      Or enter LinkedIn URN manually
                    </Button>
                  )}

                  <Collapse in={showManualEntry || pages.length === 0}>
                    <Box sx={{ mt: 1, p: 2, bgcolor: "background.default", borderRadius: 1, border: "1px solid", borderColor: "divider" }}>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Manual LinkedIn URN Entry
                      </Typography>

                      {manualEntryHelp && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          <Typography variant="caption" component="div">
                            {manualEntryHelp.description}
                          </Typography>
                          <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                            <strong>Personal profile:</strong> {manualEntryHelp.personal}
                          </Typography>
                          <Typography variant="caption" component="div">
                            <strong>Company page:</strong> {manualEntryHelp.organization}
                          </Typography>
                          {manualEntryHelp.howToFind && (
                            <Typography variant="caption" component="div" sx={{ mt: 1, fontStyle: "italic" }}>
                              {manualEntryHelp.howToFind}
                            </Typography>
                          )}
                        </Alert>
                      )}

                      <TextField
                        fullWidth
                        label="LinkedIn Author URN"
                        placeholder="urn:li:person:ABC123 or urn:li:organization:12345"
                        value={manualAuthorUrn}
                        onChange={(e) => {
                          setManualAuthorUrn(e.target.value);
                          // Clear page selection when using manual entry
                          if (e.target.value) {
                            setSelectedPage(null);
                          }
                        }}
                        error={manualAuthorUrn.length > 0 && !isValidManualUrn}
                        helperText={
                          manualAuthorUrn.length > 0 && !isValidManualUrn
                            ? "URN must start with urn:li:person: or urn:li:organization:"
                            : "Enter your LinkedIn URN to publish content"
                        }
                        size="small"
                      />

                      {isValidManualUrn && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          <Typography variant="caption">
                            Valid URN format. Click "Install" to save this integration.
                          </Typography>
                        </Alert>
                      )}
                    </Box>
                  </Collapse>
                </Box>
              )}
            </Box>
          )}

          {/* Show selected page confirmation */}
          {selectedPage && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                Selected: <strong>{selectedPage.name || selectedPage.username}</strong>
                <br />
                Click "Install" to save this integration.
              </Typography>
            </Alert>
          )}
        </>
      )}
    </Box>
  );
}
